/**
 * 🔴 **THE PINKSY APP, AS A STATIC FACT** (D118, PNK-121).
 *
 * This file is what `GET /tenant-apps/resolve` used to answer. It is not a port of that endpoint —
 * it is the recognition that the endpoint had exactly one row to serve.
 *
 * **What was actually there.** `tenantApps` held ONE row: `desk` · `pinksy.metaship.ai` · PRODUCT ·
 * LIVE. Behind it stood 4 028 lines of `services/tenant`, three tables, a per-hostname cache, an
 * admin CRUD with its own validation and mapper layers, and thirty-odd migrations — a multi-tenant
 * resolver, serving a constant. The multi-tenancy was inherited from Metaship's shell (D28) and
 * Pinksy never grew a second tenant, so every one of those parts existed to make a value
 * configurable that nothing ever configured.
 *
 * **Two of the three tables held values this repository already knows.**
 *   - `tenantImportMapEntries` held eleven URLs of the form `/modules/<name>/<version>/spa.js`. All
 *     eleven were byte-identical to what `tools/module-url.ts` computes from the module's own
 *     `package.json` — a function that has existed, and been tested, the whole time. Keeping the copy
 *     in a database is why migrations 068, 069, 070, 072, 073, 075, 076 and 078 exist: eight schema
 *     changes whose entire content is a version number that a build step derives for free and cannot
 *     get wrong. See `importMap.ts`.
 *   - `tenantAppModules` held eleven `importKey → pathPrefix` pairs, every one of them
 *     `matchMode: "prefix"`, `overlay: false`, `allowedItems: null`. Ten of the eleven prefixes are
 *     the module's own directory name.
 *
 * **The third table held real content** — branding, 24 home tiles with 23 sub-items, and the
 * sign-in page's links. That is editorial copy, and it is below, in full. It did not need a database;
 * it needed somewhere to live that a person can read and review in a diff.
 *
 * 🔴 **THE PROOF IS A GOLDEN FILE, NOT AN ARGUMENT.** `__tests__/resolve.golden.json` is the exact
 * payload the running backend answered on 2026-09-14, captured before anything was deleted, and
 * `__tests__/manifest.test.ts` asserts this file reproduces it field for field. D118's own sequencing
 * note is what that test exists to satisfy: *the hard part is not the deletion but proving that the
 * branding, the sitemap and the login page come out identical on the other side.*
 *
 * **`loginPage.termsUrl` was preserved through the refactor and is now CORRECTED (PNK-123).** It
 * pointed at `https://pinksy.metaship.ai/terms`, a host whose DNS was torn down (D60). The
 * correction is `null` rather than another address, because on 2026-09-15 there is nowhere true to
 * point it: `pinksy.metaship.ai`, `pinksy.ai` and `pinksy.net` all fail to resolve — `pinksy.ai` is
 * NXDOMAIN — and the only Pinksy host that answers is `admin.pinksy.net`, which is this product, not
 * a marketing site with a terms page. `BottomRail.vue` already renders the link behind `v-if` for
 * exactly this case, and says why: *a dead link is worse than no link*.
 *
 * The divergence from the golden is named in `__tests__/manifest.test.ts`'s `SINCE_CAPTURE`, which is
 * how this file is allowed to move without the golden being edited.
 */
import type { ResolvedMfe, AppBranding, AppHomePage, AppHomeTile, AppLoginPage } from "@/app/types";

/**
 * The app's own name — the document title, the PWA name, and the App Home welcome heading.
 *
 * 🔴 The `: string` on this and the four constants below is deliberate, not noise. Without it
 * TypeScript infers the literal type `"Pinksy Desk"`, and every consumer's comparison against
 * another string becomes an error about two literals rather than a check of two values — which also
 * stops a test substituting a different name through `src/test/appManifest.ts`.
 */
export const APP_NAME: string = "Pinksy Desk";

/**
 * The install identity. It keyed the service worker's cache and the PWA manifest's `id`, so it is
 * kept verbatim: changing it would orphan the caches and installed icons of every browser that has
 * the Desk on a home screen today.
 */
export const APP_SLUG: string = "desk";

/** Where a session lands when the path does not already name a module (`nav/routeGuard.ts`). */
export const APP_HOME_PATH: string = "/productions";

/**
 * Pinksy's own brand. `colours` is sparse on purpose and always was: a null means "the shell's
 * default stands" (`tenant/branding.ts` owns those), so this states the three colours that differ
 * and stays silent about the neutrals rather than freezing a second copy of them here.
 */
export const APP_BRANDING: AppBranding = {
  colours: {
    primary: "#F3418A",
    secondary: "#89ADD9",
    tertiary: "#D6B24C",
    background: null,
    surface: null,
    navbar: null,
  },
  defaultTheme: "light",
  identity: "own",
  iconUrl: "/brand/icon.png",
  logoLightUrl: "/brand/logo-light.png",
  logoDarkUrl: "/brand/logo-dark.png",
  logoWideLightUrl: "/brand/logo-wide-light.png",
  logoWideDarkUrl: "/brand/logo-wide-dark.png",
};

/** The copy and links around the sign-in button. `mode: "default"` is the shell's built-in hero. */
export const APP_LOGIN_PAGE: AppLoginPage = {
  mode: "default",
  htmlUrl: null,
  fileName: null,
  updatedAt: null,
  signInLabel: null,
  /**
   * 🔴 **`pinksy.ai` DOES NOT EXIST** (PNK-127). This read `support@pinksy.ai`, a domain that is
   * NXDOMAIN — so the one link a locked-out person reaches for went to an address no mail could
   * ever be delivered to. The domain is `pinksy.net`, and the group now exists on it.
   */
  supportEmail: "support@pinksy.net",
  supportUrl: null,
  /**
   * 🔴 PNK-123 said "null until there is a terms page to point at". There is one now (PNK-140):
   * static pages in `apps/shell/public/`, served ahead of the SPA fallback by nginx's
   * `try_files $uri /index.html`. They are also what Google's consent screen links to, which is
   * why they had to be real pages rather than a route that returns the app.
   */
  termsUrl: "https://pinksy.net/terms.html",
  privacyUrl: "https://pinksy.net/privacy.html",
  poweredBy: false,
};

/**
 * 🔴 **THE SITEMAP: which module answers which path.** Order is the order the rows carried
 * (`sortOrder` 10…100), and it is load-bearing — `nav/navSections.ts` renders the rail in it.
 *
 * Every entry is `matchMode: "prefix"`, `overlay: false`, `allowedItems: null`, which is what all
 * eleven rows said. The three fields are still written out per module rather than defaulted: they
 * are what single-spa is registered with (`spa/registry.ts`), and a sitemap that hides its own
 * matching rule behind a default is a sitemap you have to read two files to understand.
 *
 * Ten of the eleven prefixes are the module's directory name. `@pinksy/organisations` at `/contacts`
 * is the one that is not — migration 036/037 renamed the ROUTE when "organisations" became
 * "Contacts" in the language of the product, and left the module's own name alone.
 */
export const APP_MODULES: ResolvedMfe[] = [
  { importKey: "@pinksy/productions", pathPrefix: "/productions", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/analytics", pathPrefix: "/analytics", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/people", pathPrefix: "/people", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/organisations", pathPrefix: "/contacts", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/management", pathPrefix: "/management", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/artists", pathPrefix: "/artists", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/deals", pathPrefix: "/deals", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/time", pathPrefix: "/time", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/costs", pathPrefix: "/costs", matchMode: "prefix", overlay: false, allowedItems: null },
  // PNK-246 (project P-PNK-118): paying suppliers, crew and cast. Registered always, so the import map
  // (derived from `modules/<name>`) and the sitemap agree; its rail rows are behind `paymentsTilesOn`.
  { importKey: "@pinksy/payments", pathPrefix: "/payments", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/admin", pathPrefix: "/admin", matchMode: "prefix", overlay: false, allowedItems: null },
  { importKey: "@pinksy/profile", pathPrefix: "/profile", matchMode: "prefix", overlay: false, allowedItems: null },
];

/** The App Home heading and standfirst (`shell/views/AppHome.vue`). */
export const APP_WELCOME_TITLE: string = "Pinksy Desk";
export const APP_WELCOME_TEXT: string = "Budget in, people by link, scenes, calls, time, money.";

/**
 * 🔴 **THE TILES ARE THE SIDEBAR** (D32, D51). A tile is both a card on App Home and a rail entry —
 * `nav/navFromTenant.ts` derives one from the other, which is why there is no second list.
 *
 * `scope` says whether a destination belongs to the app or to ONE production; `section` is the
 * small-caps header consecutive tiles group under; `items` makes a tile a PLACE you go into, whose
 * own sections replace the rail on arrival. `standing` (production tiles only, D157) says what the
 * caller must be ON the production for the entry to be drawn — `nav/navStanding.ts` reads it, and a
 * production tile without one is never drawn.
 *
 * A tile's `route` may be a module root or a `?section=` deep link into one, so the tiles outnumber
 * the 11 modules. Every route here must fall under some `APP_MODULES` prefix, and
 * `__tests__/manifest.test.ts` fails the build if one does not — the check the database could never
 * make, because a foreign key cannot see inside a JSON column.
 */
const BASE_HOME_TILES: AppHomeTile[] = [
  {
    icon: "movie",
    title: "Productions",
    description: "Every show, and the way into one",
    route: "/productions",
    scope: "app",
    section: "Workspace",
  },
  /**
   * 🔴 **D157 (PNK-188) — THE SHOWS I AM BOOKED ON.** Productions is the company's slate; this is the
   * person's own — every production an engagement puts them on, with the next call on each. It is a
   * `/me` read, so it opens for everybody, and for somebody in no company it is the only way in to
   * their work. Before this row the list was reachable only from the phone's profile tabs.
   *
   * 🔴 **D164a — renamed My work.** It is her resume now: every show she is on, grouped by the show, each
   * part naming the agency its deal came through. Same route, so every link to it still lands.
   */
  {
    icon: "badge",
    title: "My work",
    description: "Your resume — every show you are on, and the agency each deal came through",
    route: "/profile?section=productions",
    scope: "app",
    section: "Workspace",
  },
  /**
   * 🔴 **D165a — MY DETAILS, HER OWN PLACE BESIDE HER WORK** (found live as Amalia, 2026-10-02). Her ID, bank, tax,
   * address and documents, kept once on her profile and shared with a production only when she says. It was reachable
   * only from inside her profile, so a person in no company had no door to it on the rail. A `/me` read like My work;
   * with `PINKSY_MY_DETAILS` off the screen says "Coming soon" and still who is paid on each deal — never a dead link.
   */
  {
    icon: "lock",
    title: "My details",
    description: "Your ID, bank, tax and documents — kept once, sealed, shared with a production only when you say",
    route: "/profile?section=details",
    scope: "app",
    section: "Workspace",
  },
  {
    icon: "insights",
    title: "Dashboards",
    description: null,
    route: "/analytics",
    scope: "app",
    section: "Workspace",
  },
  /**
   * 🔴 **OUTSIDE A PRODUCTION YOU LOOK THROUGH YOUR CONTACTS; YOU DO NOT GO LOOKING FOR STRANGERS**
   * (2026-09-19). Devin: *"when I'm in Infinity Films and I'm not inside a production, I'm going to
   * look through my contacts … people that I've worked with across many productions … and you could
   * have organizations that are contacts."* Finding people you have never worked with is casting,
   * which happens inside a production, so Find people and Network live in a production's People
   * section below.
   */
  {
    icon: "group",
    title: "People",
    description: "The people you have worked with, across your productions",
    route: "/people",
    scope: "app",
    section: "Contacts",
  },
  {
    icon: "domain",
    title: "Companies",
    description: "The companies you work with — agencies, suppliers, financiers",
    route: "/contacts",
    scope: "app",
    section: "Contacts",
  },
  {
    icon: "settings",
    title: "Management",
    description: "Your company's own record, its people and its keys",
    route: "/management",
    scope: "app",
    section: "Your company",
    items: [
      { name: "Company & Branding", icon: "domain", route: "/management", section: "Info" },
      { name: "Financial Information", icon: "account_balance", route: "/management?section=financial", section: "Info" },
      { name: "Contact Information", icon: "contact_mail", route: "/management?section=contact", section: "Info" },
      { name: "Network Functions", icon: "hub", route: "/management?section=functions", section: "Info" },
      { name: "Domains", icon: "language", route: "/management?section=domains", section: "Info" },
      { name: "Address", icon: "location_on", route: "/management?section=address", section: "Info" },
      { name: "History", icon: "history", route: "/management?section=history", section: "Info" },
      { name: "User Management", icon: "manage_accounts", route: "/management?section=people", section: "Workspace" },
      { name: "Billing & AI credits", icon: "payments", route: "/management?section=billing", section: "Workspace" },
      { name: "AI agents", icon: "smart_toy", route: "/management?section=agents", section: "Workspace" },
      { name: "Integrations", icon: "extension", route: "/management?section=integrations", section: "Developer" },
      { name: "API Docs", icon: "menu_book", route: "/management?section=docs", section: "Developer" },
      { name: "API Keys", icon: "key", route: "/management?section=keys", section: "Developer" },
    ],
  },
  {
    icon: "theater_comedy",
    title: "Artist Management",
    description: "The people this agency represents, where they are placed, and what they earn",
    route: "/artists",
    scope: "app",
    section: "Your company",
    items: [
      { name: "Artists", icon: "theater_comedy", route: "/artists", section: "Artist management" },
      // 🔴 D164c — gate 1 of the double gate: the agency's artists' offers, countered and sent through from here.
      { name: "Deals", icon: "handshake", route: "/artists?section=deals", section: "Artist management" },
      { name: "Earnings", icon: "payments", route: "/artists?section=earnings", section: "Artist management" },
    ],
  },
  {
    icon: "admin_panel_settings",
    title: "Admin",
    description: "Tenants, rule packs, settings",
    route: "/admin",
    scope: "app",
    section: "Settings",
    items: [
      { name: "Platform", icon: "admin_panel_settings", route: "/admin?section=platform", section: null },
      { name: "Organisations", icon: "domain", route: "/admin?section=organisations", section: "Accounts" },
      { name: "Users", icon: "manage_accounts", route: "/admin?section=users", section: "Accounts" },
      { name: "People", icon: "group", route: "/admin?section=people", section: "Accounts" },
      { name: "Productions", icon: "movie", route: "/admin?section=productions", section: "Platform" },
      { name: "Billing", icon: "receipt_long", route: "/admin?section=billing", section: "Platform" },
      { name: "Agents", icon: "vpn_key", route: "/admin?section=agents", section: "Platform" },
    ],
  },
  /**
   * The person's own record. It used to be a DERIVED row (a module with no tile of its own); the My
   * productions tile above gave `@pinksy/profile` a tile, so it is written out here instead.
   */
  {
    icon: "person",
    title: "Profile",
    description: "Your own record — what you are like, and who may read it",
    // D164a: named, because a bare `/profile` is a person's LANDING — her work, when she has no staff company.
    route: "/profile?section=profile",
    scope: "app",
    section: "Settings",
  },
  /**
   * 🔴 **D157 (PNK-187) — THE ENGAGED PERSON'S ONE DOOR.** Somebody engaged on a production as cast,
   * crew or background, with no role, share or ownership there, has no business in its working
   * documents — but it is still THEIR production. This is the only entry they are offered inside it,
   * and it leads to their own view of it in the profile (`?section=production`; `withProduction()`
   * adds the id). Everyone holding any standing on the production never sees it.
   */
  {
    icon: "badge",
    title: "My production",
    description: null,
    route: "/profile?section=production",
    scope: "production",
    section: null,
    standing: { engagedOnly: true },
  },
  {
    icon: "receipt_long",
    title: "Top sheet",
    description: null,
    route: "/costs?section=topsheet",
    scope: "production",
    section: "Budget",
    standing: { anyScope: ["budget.topsheet"] },
  },
  {
    icon: "table_rows",
    title: "Charge lines",
    description: null,
    route: "/costs?section=detail",
    scope: "production",
    section: "Budget",
    standing: { anyScope: ["budget.detail"] },
  },
  {
    icon: "tune",
    title: "What drives it",
    description: null,
    route: "/costs?section=drivers",
    scope: "production",
    section: "Budget",
    standing: { anyScope: ["budget.detail"] },
  },
  {
    icon: "photo_camera",
    title: "Petty cash",
    description: null,
    route: "/costs?section=cash",
    scope: "production",
    section: "Budget",
    standing: { anyScope: ["cost.detail"] },
  },
  {
    // D158 (PNK-189 CR-7): Netflix's S2 cost report per account. On the rail once its EFC is honest — cost
    // to date plus an ETC from a named ladder — drawn to whoever reads it (`cost.summary`: the accountant,
    // the producer, a financier share that ticked "cost report").
    icon: "summarize",
    title: "Cost report",
    description: null,
    route: "/costs?section=costReport",
    scope: "production",
    section: "Budget",
    standing: { anyScope: ["cost.summary"] },
  },
  {
    // 🔴 D153 (PNK-179): the script is the lowest level — a heading on its pages IS a scene below.
    icon: "edit_note",
    title: "Script",
    description: null,
    route: "/productions?section=script",
    scope: "production",
    section: "Schedule",
    // D157: drawn to whoever reads the scenes — the writer's `schedule.scenes` included (D154).
    standing: { anyScope: ["schedule.scenes"] },
  },
  {
    icon: "theaters",
    title: "Scenes",
    description: null,
    route: "/productions?section=scenes",
    scope: "production",
    section: "Schedule",
    standing: { anyScope: ["schedule.scenes"] },
  },
  {
    icon: "event",
    title: "Days",
    description: null,
    route: "/productions?section=days",
    scope: "production",
    section: "Schedule",
    standing: { anyScope: ["schedule.calls"] },
  },
  {
    icon: "assignment",
    title: "Call sheets",
    description: null,
    route: "/productions?section=callsheet",
    scope: "production",
    section: "Schedule",
    standing: { anyScope: ["schedule.calls"] },
  },
  {
    icon: "manage_accounts",
    title: "Team",
    description: null,
    route: "/productions?section=team",
    scope: "production",
    section: "People",
    standing: { administers: true, anyRole: ["producer"] },
  },
  {
    icon: "badge",
    title: "Staffing",
    description: null,
    route: "/productions?section=staffing",
    scope: "production",
    section: "People",
    standing: { anyScope: ["staffing.plan"] },
  },
  /** Casting and filling a position: the only place Pinksy goes looking for people you do not know. */
  {
    icon: "person_search",
    title: "Find people",
    description: "Everyone who publishes their own record — search it to cast a part or fill a position",
    route: "/people?section=directory",
    scope: "production",
    section: "People",
    standing: { anyScope: ["staffing.plan"] },
  },
  {
    icon: "hub",
    title: "Network",
    description: "Everyone who accepted one of your links, and the availability they chose to show",
    route: "/people?section=network",
    scope: "production",
    section: "People",
    standing: { anyScope: ["staffing.plan"] },
  },
  {
    icon: "handshake",
    title: "Deals",
    description: null,
    route: "/deals",
    scope: "production",
    section: "People",
    standing: { engaged: true, anyScope: ["time.approvals", "budget.detail"] },
  },
  {
    icon: "storefront",
    title: "Suppliers",
    description: null,
    route: "/productions?section=suppliers",
    scope: "production",
    section: "People",
    standing: { anyScope: ["production.summary"] },
  },
  {
    icon: "schedule",
    title: "Time",
    description: null,
    route: "/time",
    scope: "production",
    section: "Time & money",
    standing: { engaged: true, anyScope: ["time.approvals", "budget.detail"] },
  },
  /**
   * 🔴 D173 (PNK-261, PNK-262): each week's pay, forecast per person from their contract and the production calendar;
   * the line producer corrects it against what happened, finance approves it. Beside Payroll, where pay is looked for.
   * Drawn only while `PINKSY_WEEKLY_PAY` is on (PNK-274).
   */
  {
    icon: "event_note",
    title: "Weekly pay",
    description: null,
    route: "/costs?section=weeklyPay",
    scope: "production",
    section: "Time & money",
    standing: { anyScope: ["payroll.summary"], feature: "weeklyPay" },
  },
  {
    icon: "payments",
    title: "Payroll",
    description: null,
    route: "/costs?section=pay",
    scope: "production",
    section: "Time & money",
    standing: { anyScope: ["payroll.summary"] },
  },
  {
    icon: "settings",
    title: "Set up",
    description: null,
    route: "/productions?section=setup",
    scope: "production",
    section: "Settings",
    standing: { anyScope: ["production.summary"] },
  },
  /**
   * 🔴 D170 (PNK-260): the production calendar — prep, shoot blocks, hiatus, wrap and post, and what every day is.
   * Beside Set up, because the dates are what a production IS before anybody is paid.
   */
  {
    icon: "calendar_month",
    title: "Production calendar",
    description: null,
    route: "/productions?section=calendar",
    scope: "production",
    section: "Settings",
    // 🔴 Behind PINKSY_PRODUCTION_CALENDAR: drawn only while the server lists the feature (D170).
    standing: { anyScope: ["production.summary"], feature: "productionCalendar" },
  },
  /** D170a/D170b (PNK-260, PNK-267): the pay rules and who can edit what — the producer chooses and signs off. */
  {
    icon: "rule",
    title: "Production options",
    description: null,
    route: "/productions?section=options",
    scope: "production",
    section: "Settings",
    // 🔴 Behind PINKSY_PRODUCTION_CALENDAR: drawn only while the server lists the feature (D170).
    standing: { anyScope: ["payroll.summary", "production.summary"], feature: "productionCalendar" },
  },
  {
    icon: "star",
    title: "Reception",
    description: null,
    route: "/productions?section=reception",
    scope: "production",
    section: "Settings",
    standing: { anyScope: ["production.summary"] },
  },
  /**
   * 🔴 **D141 (PNK-164) — INTEGRATIONS AT THE PRODUCTION LEVEL.** Devin: *"every production can have
   * its only integrations."* `scope: "production"` is what makes it one: the rail only offers it with
   * a show open, and `withProduction()` carries `?production=` onto the route, so the screen is about
   * THIS production rather than about whichever one was last looked at.
   *
   * It sits in Settings beside Set up, and the COMPANY's own is a separate row under Management →
   * Developer. Two levels, two rails, one gallery component.
   */
  {
    icon: "extension",
    title: "Integrations",
    description: null,
    route: "/productions?section=integrations",
    scope: "production",
    section: "Settings",
    standing: { administers: true },
  },
];

/**
 * 🔴 **PAYMENTS — ONE ROW, BEHIND A BUILD FLAG** (PNK-246, project P-PNK-118).
 *
 * One "Payments" row straight after Payroll, under Time & money. It used to be four rows under their own
 * header (Approvals, EFT payments, Batches, Documents); Celia, 2026-10-15: *"I don't think there are all the
 * different sections needed anymore as we can tackle all the different sections on the new screen … can all
 * now be called payments"*. Inside, the screen walks the work itself: Upload, Approve, Pay, History.
 *
 * Drawn to whoever captures payments (`cost.detail`: the accountant) or approves them (`cost.approvals`, which
 * "lets its holder MOVE MONEY"). Hidden unless the shell was BUILT with `VITE_PINKSY_PAYMENTS=on` — pinksy.net
 * never is, and a developer sets it in `apps/shell/.env.development.local` (gitignored) — `.development.`, so
 * `vite` (the dev server) reads it and vitest (mode `test`) does not, and the tests keep seeing pinksy.net's
 * tiles. The module says "not switched on" on its own when it was built without it, so a pasted `/payments`
 * link is never a live screen there.
 */
export const PAYMENTS_FLAG = "VITE_PINKSY_PAYMENTS";

export const PAYMENTS_TILES: AppHomeTile[] = [
  { icon: "account_balance_wallet", title: "Payments", description: null, route: "/payments", scope: "production", section: "Time & money", standing: { anyScope: ["cost.detail", "cost.approvals"] } },
];

/** `on`, `true`, `1`, `yes` — the words `services/user/onboarding-flag.ts` accepts — and nothing else. */
export const paymentsTilesOn = (env: Readonly<Record<string, unknown>>): boolean =>
  ["on", "true", "1", "yes"].includes(String(env[PAYMENTS_FLAG] ?? "").trim().toLowerCase());

/** The tiles with the Payments rows placed after Payroll when the flag is on; the base list untouched when off. */
export function homeTiles(env: Readonly<Record<string, unknown>>): AppHomeTile[] {
  if (!paymentsTilesOn(env)) return BASE_HOME_TILES;
  const payroll = BASE_HOME_TILES.findIndex((tile) => tile.route === "/costs?section=pay");
  const at = payroll === -1 ? BASE_HOME_TILES.length : payroll + 1;
  return [...BASE_HOME_TILES.slice(0, at), ...PAYMENTS_TILES, ...BASE_HOME_TILES.slice(at)];
}

export const APP_HOME_TILES: AppHomeTile[] = homeTiles(import.meta.env);

/**
 * Registered modules the rail must NOT derive a row for — the ones whose tiles a flag is holding back.
 * Without this, a module with no tile gets a plain row named after it (`nav/navForApp.ts`), and the
 * flag would hide the four Payments rows only to put a fifth, ungated one on the app rail.
 */
export const unlistedModulePrefixes = (env: Readonly<Record<string, unknown>>): string[] => (paymentsTilesOn(env) ? [] : ["/payments"]);

export const UNLISTED_MODULE_PREFIXES: readonly string[] = unlistedModulePrefixes(import.meta.env);

/**
 * The App Home page as one object — the heading, the standfirst and the tiles. `AppHome.vue`,
 * `Login.vue` and `Sidebar.vue` each used to read `session.tenant?.homePage`, an optional field of an
 * optional field; there is one home page and it is always there.
 *
 * `enabled` is kept, and kept `true`. It was the switch that turned the tile grid off for a tenant
 * whose landing screen was its `homePath` module instead, and `nav/routeGuard.ts` still reads it — so
 * it stays a field with a value rather than an assumption spread across four callers.
 */
export const APP_HOME_PAGE: AppHomePage = {
  enabled: true,
  welcomeTitle: APP_WELCOME_TITLE,
  welcomeText: APP_WELCOME_TEXT,
  tiles: APP_HOME_TILES,
};
